/**
 * Nearest available vehicles to the scene.
 *
 * The thing QContact and Animo cannot do, and the reason the Cartrack
 * integration exists: a dispatcher picks a crew without alt-tabbing into a
 * tracking portal and eyeballing a map.
 *
 * Two things are stated on screen rather than assumed, because both can send a
 * crew to the wrong place if a dispatcher assumes otherwise:
 *
 *   - the distance is STRAIGHT LINE, not drive time
 *   - the GPS fix has an age, and some of them are hours or days old
 *
 * A stale vehicle is shown, greyed and labelled, never silently ranked first.
 * Hiding it would leave a dispatcher with a short list and no idea why.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type NearestVehicles as Data } from '../api/client.js';

function fixAge(minutes: number): string {
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)} days ago`;
}

export function NearestVehicles({ caseId }: { caseId: string }) {
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['nearest', caseId],
    queryFn: () => api.get<Data>(`/cases/${caseId}/nearest-vehicles?limit=8`),
    // Positions move. Cheap query, and a dispatcher looking at this screen wants
    // it current rather than as it was when they opened the case.
    refetchInterval: 30_000,
  });

  const assign = useMutation({
    mutationFn: (vehicleId: string) =>
      api.post(`/cases/${caseId}/assignments`, { vehicleId, role: 'crew' }),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['case', caseId] }),
        qc.invalidateQueries({ queryKey: ['nearest', caseId] }),
        qc.invalidateQueries({ queryKey: ['vehicles'] }),
      ]);
    },
  });

  if (isLoading) return <p className="text-xs text-slate-500">Locating vehicles…</p>;
  if (error) return <p className="text-xs text-red-400">{error.message}</p>;
  if (!data) return null;

  // No scene coordinates yet — say what to do about it rather than showing an
  // arbitrary list that looks like an answer to a question nobody could ask.
  if (data.reason) {
    return <p className="text-xs text-amber-400">{data.reason}</p>;
  }

  if (data.vehicles.length === 0) {
    return (
      <p className="text-xs text-slate-500">
        No vehicles are reporting a position. Check the tracking integration.
      </p>
    );
  }

  return (
    <div>
      <ul className="space-y-1">
        {data.vehicles.map((v) => (
          <li key={v.id}>
            <button
              type="button"
              disabled={assign.isPending}
              onClick={() => assign.mutate(v.id)}
              className={
                'flex w-full items-center gap-3 rounded px-2 py-1.5 text-left hover:bg-slate-800 disabled:opacity-40 ' +
                (v.stale ? 'opacity-60' : '')
              }
            >
              <span className="w-14 shrink-0 font-mono text-sm text-slate-100">{v.fleet_no}</span>

              <span className="w-16 shrink-0 text-right text-sm tabular-nums text-slate-200">
                {v.distance_km} km
              </span>

              <span className="min-w-0 flex-1 truncate text-xs text-slate-400">
                {v.place_description ?? '—'}
              </span>

              <span className="flex shrink-0 items-center gap-1.5">
                {v.ignition && !v.stale && (
                  <span className="rounded bg-emerald-600/20 px-1.5 py-0.5 text-[10px] font-medium text-emerald-300">
                    moving
                  </span>
                )}
                {v.on_another_case && (
                  <span className="rounded bg-amber-600/20 px-1.5 py-0.5 text-[10px] font-medium text-amber-300">
                    on a case
                  </span>
                )}
                <span
                  className={`text-[10px] tabular-nums ${v.stale ? 'text-amber-400' : 'text-slate-500'}`}
                >
                  {fixAge(v.fix_age_minutes)}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>

      <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
        Straight-line distance, not drive time — the closest vehicle is not always the fastest.
        Dimmed rows last reported over {data.staleAfterMinutes} minutes ago and may have moved
        since.
      </p>
    </div>
  );
}
